import type { ReactElement } from 'react'
import { useMemo } from 'react'
import { formatMinor } from '~/api/client'
import { chartCss } from '~/charts/theme'
import { Card, CardHead, StatCard } from '~/components/ui/Card'
import type { ChipTone, Notice } from '~/components/ui/Status'
import { NoticeList, StatusPill } from '~/components/ui/Status'
import { DataTable } from '~/components/ui/Table'
import { confirmedAtNote } from '~/state/sources'
import type { Consent as ConsentState, CredentialsSource, SourceState } from '~/state/sources'
import { describeAge, describeDuration } from '~/state/dates'
import type { HealthReport, SourceHealth, SourceTone } from '~/state/health'
import { useHealth } from '~/state/queries'
import { useSources } from '~/state/sources'

const TONES: { tone: SourceTone; label: string; note: string }[] = [
  {
    tone: 'healthy',
    label: 'Reporting',
    note: 'Fetched their own positions for this date.',
  },
  {
    tone: 'stale',
    label: 'Carried forward',
    note: 'Last positions repeated. The figure is real, but it is old.',
  },
  {
    tone: 'failed',
    label: 'Never reported',
    note: 'Contributing nothing, which is not the same as contributing zero.',
  },
  {
    tone: 'idle',
    label: 'Not configured',
    note: 'No credentials yet. Waiting, not broken.',
  },
]

const LABEL_OF: Record<SourceTone, string> = {
  healthy: 'Reporting',
  stale: 'Carried forward',
  failed: 'Never reported',
  idle: 'Not configured',
}

const PAGE_CSS = `
.nw-health .nw-stats { display: flex; flex-wrap: wrap; gap: var(--nw-space-6); margin-bottom: var(--nw-stack); }
.nw-health .nw-stats > * { flex: 1 1 220px; }
/* A source's row carries a status, a date, a summary, a value and whatever is
   known about its access — four of those run to several lines, so the row is as
   tall as what is in it rather than one of the table's own. */
.nw-health .nw-data :where(td, tbody th) { height: auto; padding-bottom: 10px; padding-top: 10px; vertical-align: top; }
.nw-health small { color: var(--nw-muted); display: block; font-size: var(--nw-text-chip); }
.nw-health .nw-consent { list-style: none; margin: 0; padding: 0; }
.nw-health .nw-consent li { padding: 2px 0; }
.nw-health .nw-unpriced { color: var(--nw-muted); font-size: var(--nw-text-chip); list-style: none; margin: var(--nw-space-2) 0 0; padding: 0; }
.nw-health .nw-unpriced li { padding: 2px 0; }
.nw-health .nw-data tr[data-tone='idle'] :where(th, td) { color: var(--nw-muted); }
`

const SOURCE_COLUMNS = [
  { id: 'source', label: 'Source', width: '150px' },
  { id: 'status', label: 'Status', width: '190px' },
  { id: 'collected', label: 'Last collected', width: '190px' },
  { id: 'returned', label: 'Returned', width: '190px' },
  { id: 'value', label: 'Value', width: '160px', align: 'right' as const },
  { id: 'access', label: 'Access' },
]

/** Reporting, carried forward, never reported, waiting — over the system's four tones. */
const TONE_OF: Record<SourceTone, ChipTone> = {
  healthy: 'live',
  stale: 'caution',
  failed: 'failed',
  idle: 'manual',
}

export function Health(): ReactElement {
  const { data, error, isPending } = useHealth()
  const sources = useSources()

  return (
    <main className="nw nw-health">
      <style>{chartCss + PAGE_CSS}</style>
      <h1>Source health</h1>
      {data && (
        <p className="nw-sub">
          {`${data.sources.length} sources · valued as of ${data.date}${
            data.unpriced > 0
              ? ` · ${data.unpriced} holding${data.unpriced === 1 ? '' : 's'} without a price, in no figure below`
              : ''
          } · all figures in ${data.baseCurrency}`}
        </p>
      )}

      {isPending && <p className="nw-empty">Loading…</p>}
      {error && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Could not load source health: {error.message}</span>
        </p>
      )}
      {data && <Report report={data} sources={sources.data?.sources ?? []} />}
    </main>
  )
}

function Report({
  report,
  sources,
}: {
  report: HealthReport
  sources: readonly SourceState[]
}): ReactElement {
  // Keyed by the collector, the same name `SourceHealth` carries, so a row
  // whose figures are recorded under a different collector than its own id —
  // hand entry is `manual` — still finds its own access here.
  const byCollector = useMemo(
    () => new Map(sources.map((source) => [source.collector ?? source.id, source])),
    [sources],
  )
  const notices = useMemo(() => credentialNotices(sources), [sources])

  return (
    <>
      <div className="nw-stats">
        {TONES.map((entry) => (
          <StatCard key={entry.tone} label={entry.label} note={entry.note}>
            {report.counts[entry.tone]}
          </StatCard>
        ))}
      </div>

      {(report.attention.length > 0 || notices.length > 0) && (
        <Card>
          <CardHead title="Needs a hand" />
          <NoticeList notices={handNotices(report, notices)} />
        </Card>
      )}

      {report.counts.idle > 0 && (
        <p className="nw-note">
          {report.counts.idle === 1 ? 'One source has' : `${report.counts.idle} sources have`} no
          credentials yet. Nothing is missing from the total, because nothing is expected from them
          — they are waiting, not broken.
        </p>
      )}

      <Card>
        <CardHead
          title="Every source"
          lede="A source that stops reporting carries its last positions forward, so a failure never reads as a loss. The cost of that is silence: the total stays confident while the data behind it ages. This is where the silence is broken."
        />
        <DataTable columns={SOURCE_COLUMNS} label="Every source and where it stands">
          {report.sources.map((source) => (
            <Row
              key={source.source}
              source={source}
              access={byCollector.get(source.source)}
              baseCurrency={report.baseCurrency}
            />
          ))}
        </DataTable>
      </Card>

      <Card>
        <CardHead title="Pricing" />
        {report.priceRunError !== null && (
          <p className="nw-flag nw-flag--missing">
            <span className="nw-flag-icon" aria-hidden="true">
              ✕
            </span>
            <span>Last night's pricing run did not finish: {report.priceRunError}</span>
          </p>
        )}
        {report.missingPrices.length === 0 &&
          report.missingFx.length === 0 &&
          !report.untaggedCostBasis && (
            <p className="nw-note">
              Everything held on {report.date} had a price and, where it was needed, a rate.
            </p>
          )}
        {(report.missingPrices.length > 0 || report.missingFx.length > 0) && (
          <p className="nw-flag nw-flag--missing">
            <span className="nw-flag-icon" aria-hidden="true">
              ✕
            </span>
            <span>
              No price for {report.missingPrices.join(', ') || 'nothing'}
              {report.missingFx.length > 0 && `; no rate for ${report.missingFx.join(', ')}`}. Those
              holdings are left out of the totals rather than counted as zero.
            </span>
          </p>
        )}
        {/* Said apart from the line above because it is the opposite case: the
            holding is counted in full, and it is the gain on it that cannot be
            worked out. Folded in, it would read as value that had gone missing. */}
        {report.untaggedCostBasis && (
          <p className="nw-flag nw-flag--missing">
            <span className="nw-flag-icon" aria-hidden="true">
              ✕
            </span>
            <span>
              A cost basis somewhere does not say which currency it was paid in. Those holdings
              still count in full; no gain, and no deferred tax, is worked out on them until it
              does.
            </span>
          </p>
        )}
        {/* The count above answers "how many"; this answers "which ones", read
            straight off `GET /holdings`' own `unpriced[]` rather than repeated
            here as a second guess at it. */}
        {report.unpricedHoldings.length > 0 && (
          <ul className="nw-unpriced">
            {report.unpricedHoldings.map((holding, index) => (
              <li key={`${holding.symbol}-${holding.accountName}-${index}`}>
                {holding.instrumentName} in {holding.accountName} —{' '}
                {holding.reason === 'no_price' ? 'no price yet' : 'priced, but no rate to convert it'}
              </li>
            ))}
          </ul>
        )}
        {report.carriedFx.length > 0 && (
          <p className="nw-flag nw-flag--stale">
            {report.carriedFx.join(', ')} priced off the last rate the ECB published, not one
            confirmed today.
          </p>
        )}
        {report.projectedMinor !== 0 && (
          <p className="nw-note">
            {formatMinor(report.projectedMinor, report.baseCurrency)} of the total rests on
            positions nobody confirmed today.
          </p>
        )}
      </Card>
    </>
  )
}

function Row({
  source,
  access,
  baseCurrency,
}: {
  source: SourceHealth
  access: SourceState | undefined
  baseCurrency: string
}): ReactElement {
  const idle = source.tone === 'idle'

  return (
    <tr data-tone={source.tone}>
      <th scope="row">{source.source}</th>
      <td>
        <StatusPill tone={TONE_OF[source.tone]} label={LABEL_OF[source.tone]} />
        {source.error && <small>{source.error}</small>}
      </td>
      <td>
        {source.lastCollected === null ? (
          idle ? (
            'nothing yet'
          ) : (
            'never'
          )
        ) : (
          <>
            {source.lastCollected}
            {source.staleDays !== null && source.staleDays > 0 && (
              <small>carried forward for {describeDuration(source.staleDays)}</small>
            )}
          </>
        )}
      </td>
      <td>
        {source.summary}
        {source.projected > 0 && <small>{source.projected} projected</small>}
        {source.unpriced > 0 && <small>{source.unpriced} without a price</small>}
      </td>
      <td className="nw-num">
        {source.valueMinor === null ? '—' : formatMinor(source.valueMinor, baseCurrency)}
        {source.valueMinor !== null && source.unpriced > 0 && (
          <small>{source.unpriced} not included</small>
        )}
      </td>
      <td>
        <Access source={access} idle={idle} />
      </td>
    </tr>
  )
}

/**
 * What `/sources` itself says about this source's access, read off the same
 * response Sources draws its own row from rather than a second settings key —
 * the `sourceAccess` map this used to read from nothing ever wrote (PRO-233).
 */
function Access({ source, idle }: { source: SourceState | undefined; idle: boolean }): ReactElement {
  if (source === undefined) {
    return <small>{idle ? 'awaiting credentials' : 'no expiry to watch'}</small>
  }

  if (source.kind === 'credentials' && source.attention !== undefined) {
    return (
      <div className="nw-session">
        <StatusPill tone="failed" label="Failed" glyph />
        <small>{source.attention}</small>
      </div>
    )
  }

  const confirmedAt = source.kind === 'credentials' ? oldestConfirmation(source) : undefined
  if (confirmedAt !== undefined) {
    return <small>{confirmedAtNote(confirmedAt)}</small>
  }

  if (source.kind === 'consent' && source.consents.length > 0) {
    return (
      <ul className="nw-consent">
        {source.consents.map((consent) => (
          <ConsentRow key={`${consent.bank}-${consent.validUntil}`} consent={consent} />
        ))}
      </ul>
    )
  }

  return <small>{idle ? 'awaiting credentials' : 'no expiry to watch'}</small>
}

/**
 * The least recent of a source's confirmations, where it holds several
 * connections. This page gives a source one line, and the connection proved
 * longest ago is the one that line would be wrong to leave out — the most
 * recent would let a stale second login hide behind a freshly renewed first.
 */
function oldestConfirmation(source: CredentialsSource): string | undefined {
  const confirmations = (source.connections ?? [])
    .map((row) => row.credentialConfirmedAt)
    .filter((at): at is string => at !== undefined)
    .sort()

  return confirmations[0]
}

/**
 * A bank consent's own standing, drawn as the same chip Sources uses for it —
 * a glyph carries the meaning here too, rather than the colour a bare
 * `data-state` attribute used to carry alone.
 */
function ConsentRow({ consent }: { consent: ConsentState }): ReactElement {
  if (consent.state === 'active') {
    return (
      <li>
        {consent.bank}: {describeDuration(consent.daysRemaining)} left
      </li>
    )
  }

  const tone: ChipTone = consent.state === 'expired' ? 'failed' : 'caution'
  const label =
    consent.state === 'expired' ? 'Expired' : `Expires in ${consent.daysRemaining} days`

  return (
    <li>
      {consent.bank}:{' '}
      <StatusPill tone={tone} label={label} glyph />
      <small>Consent runs to {consent.validUntil}.</small>
    </li>
  )
}

/**
 * Every source worth a look, in one list — a lapsed credential's own
 * `attention` and a bank consent close enough to `validUntil` to warn about
 * before it lapses.
 *
 * Read off the same `/sources` response Sources itself draws its "needs a
 * hand" panel from, so this page never says something about a source that
 * Sources does not already say — one channel, read twice, rather than a
 * second answer kept beside it.
 */
function credentialNotices(sources: readonly SourceState[]): Notice[] {
  const notices: Notice[] = []
  for (const source of sources) {
    if (source.kind !== 'collection' && source.attention !== undefined) {
      notices.push({ id: source.id, tone: 'critical', text: `${source.label}: ${source.attention}` })
    }
    if (source.kind === 'consent') {
      for (const consent of source.consents) {
        if (consent.state !== 'expiring') continue
        notices.push({
          id: `${source.id}:${consent.bank}:${consent.holder}`,
          tone: 'warning',
          text: `${source.label} — ${consent.bank}: expires in ${consent.daysRemaining} days.`,
        })
      }
    }
  }
  return notices
}

/**
 * Every source that wants a hand, as one list. Four full-width alarm bars read
 * as an outage, and a consent with a fortnight left on it is not one.
 */
function handNotices(report: HealthReport, notices: readonly Notice[]): Notice[] {
  return [
    ...report.attention.map((source) => ({
      id: source.source,
      tone: 'critical' as const,
      // One text run, because it is one sentence: a name set in bold and the rest
      // of it beside reads as a label and a value, which it is not.
      text: `${source.source} ${
        source.lastCollected === null
          ? 'has never reported'
          : `last reported ${describeAge(source.staleDays ?? 0)} (${source.lastCollected})`
      }${source.error ? ` — ${source.error}` : ''}`,
    })),
    ...notices,
  ]
}
